/**
 * Normalize media URLs so <img src> works from the SPA dev server (Vite proxies `/storage` to Laravel).
 * Encodes media paths so filenames with spaces/special chars load correctly.
 * We intentionally do NOT rewrite absolute URLs to same-origin paths; the backend's
 * absolute URLs are already working on the View page.
 */
export function normalizeProductMediaUrlForBrowser(url: string | undefined): string | undefined {
  if (url == null) return undefined;
  const s = String(url).trim();
  if (!s) return undefined;
  if (s.startsWith("blob:") || s.startsWith("data:")) return s;

  if (s.startsWith("//")) {
    if (typeof window !== "undefined") return `${window.location.protocol}${s}`;
    return `https:${s}`;
  }

  try {
    if (/^https?:\/\//i.test(s)) {
      const u = new URL(s);
      const encodedSegments = u.pathname
        .split("/")
        .map((seg) => (seg ? safeEncodePathSegment(seg) : seg))
        .join("/");
      u.pathname = encodedSegments;
      return u.toString();
    }
  } catch {
    /* ignore */
  }

  let path = s.startsWith("/") ? s : `/${s}`;
  if (path.startsWith("/uploads") && !path.startsWith("/storage/")) {
    path = `/storage${path}`;
  }
  if (path.startsWith("/storage")) {
    return encodeMediaPath(path);
  }
  return path;
}

function safeEncodePathSegment(seg: string): string {
  if (!seg) return seg;
  try {
    return encodeURIComponent(decodeURIComponent(seg));
  } catch {
    return encodeURIComponent(seg);
  }
}

/** Encode path segments so filenames with spaces (e.g. "image (3).jpg") load correctly. */
function encodeMediaPath(full: string): string {
  let path = full;
  let suffix = "";
  const qi = full.indexOf("?");
  const hi = full.indexOf("#");
  if (qi !== -1) {
    suffix = full.slice(qi);
    path = full.slice(0, qi);
  } else if (hi !== -1) {
    suffix = full.slice(hi);
    path = full.slice(0, hi);
  }
  const encoded = path
    .split("/")
    .map((seg) => (seg ? safeEncodePathSegment(seg) : seg))
    .join("/");
  return encoded + suffix;
}
